import React, { useEffect, useState } from "react"
import { fetchReimbursementDetails } from "actions/reimbursementDetailsActions"
import { useDispatch, useSelector } from "react-redux"
import { getReimbursementDetails } from "reducers/reimbursementDetails"
import { useFeatureToggle } from "../feature-toggle/FeatureToggleContext"
import CarrotClient from "lib/carrot-api"
import Settings from "utils/CarrotConfig"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

export const CSD_CONFIGURATION = {
  NEITHER: "NEITHER",
  MEMBER_ONLY: "MEMBER_ONLY",
  PARTNER_ONLY: "PARTNER_ONLY",
  MEMBER_AND_PARTNER_SEPARATE: "MEMBER_AND_PARTNER_SEPARATE",
  MEMBER_AND_PARTNER_COMBINED: "MEMBER_AND_PARTNER_COMBINED"
}

export const COMPANY_DEDUCTIBLE_STATUS = {
  CARROT_SPECIFIC_DEDUCTIBLE: "CARROT_SPECIFIC_DEDUCTIBLE",
  EXTERNAL: "EXTERNAL",
  NO_DEDUCTIBLE: "NO_DEDUCTIBLE"
}
const DeductibleContext = React.createContext(null)

export const useUserDeductible = () => React.useContext(DeductibleContext)

function useDebugTools() {
  const { showNewEnrollmentFlow } = useFeatureToggle()
  const [csdConfiguration, setCsdConfiguration] = useState(CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED)
  const [companyDeductibleStatus, setCompanyDeductibleStatus] = useState(
    COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE
  )
  const debugGroupLabel = "Deductible Debug Command"
  function mountDebugTools(): void {
    function log(message): void {
      console.group(debugGroupLabel)
      console.info(message)
      console.groupEnd()
    }

    // eslint-disable-next-line
    // @ts-ignore
    window.deductible = {
      updateCSDConfiguration: (csdConfigurationType) => {
        const newCSDConfigurationVal =
          CSD_CONFIGURATION[csdConfigurationType] || CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED
        log(`Updating $csdConfiguration [${csdConfiguration}] -> [${newCSDConfigurationVal}]`)
        setCsdConfiguration(newCSDConfigurationVal)
      },
      getCSDConfiguration: () => {
        log(`$csdConfiguration [${csdConfiguration}]`)
      },
      updateCompanyDeductibleStatus: (companyDeductibleStatusType) => {
        const newCompanyDeductibleStatusVal =
          COMPANY_DEDUCTIBLE_STATUS[companyDeductibleStatusType] || COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE
        log(`Updating $companyDeductibleStatus [${companyDeductibleStatus}] -> [${newCompanyDeductibleStatusVal}]`)
        setCompanyDeductibleStatus(newCompanyDeductibleStatusVal)
      },
      getCompanyDeductibleStatus: () => {
        log(`$companyDeductibleStatus [${companyDeductibleStatus}]`)
      }
    }
  }
  useEffect(() => {
    if (showNewEnrollmentFlow) {
      console.info("%c⚠️ Enabling deductible debug tools. Check out the 'deductible' window object", "color:Orange")
    }
  }, [])
  useEffect(() => {
    if (showNewEnrollmentFlow) {
      mountDebugTools()
    }
  }, [csdConfiguration, companyDeductibleStatus])

  return {
    companyDeductibleStatus: companyDeductibleStatus,
    csdConfiguration: csdConfiguration,
    partnerDeductible: {
      deductibleSnapshot: {
        amountAppliedToDeductible: 0,
        amountRemaining: 140000,
        carrotAmountAppliedToDeductible: 0,
        externalAmountAppliedToDeductible: 0,
        planEndDate: "2021-12-31",
        planStartDate: "2021-01-01",
        totalAmount: 140000
      },
      displayDeductibleBreakdown: true,
      sourceName: "CarrotSpecificDeductibleSource"
    },
    deductible: {
      displayDeductibleBreakdown: true,
      deductibleSnapshot: {
        totalAmount: 140000,
        amountAppliedToDeductible: 0,
        carrotAmountAppliedToDeductible: 0,
        externalAmountAppliedToDeductible: 0,
        amountRemaining: 140000,
        planStartDate: "2021-01-01",
        planEndDate: "2021-12-31"
      },
      sourceName: "CarrotSpecificDeductibleSource"
    }
  }
}

export function DeductibleProvider({ children }): JSX.Element {
  const dispatch = useDispatch()
  const mockDeductibleInfo = useDebugTools()
  const [companyDeductibleStatus, setCompanyDeductibleStatus] = useState(null)
  const [csdConfiguration, setCsdConfiguration] = useState(null)

  // Mocked info prior to real hookup
  const reimbursementDetails = useSelector(getReimbursementDetails)
  const deductiblePreview = {
    companyDeductibleStatus,
    csdConfiguration
  }

  async function retrieveDeductiblePreview(): Promise<void> {
    const payload = await client.getBenefitEnrollmentPreview()
    setCompanyDeductibleStatus(payload.companyDeductibleStatus)
    setCsdConfiguration(payload.csdConfiguration)
  }

  function refreshUserInfo() {
    return dispatch(fetchReimbursementDetails())
  }

  async function updateUserBenefitEnrollment(): Promise<void> {
    await client.enrollUserBenefitEnrollment()
  }

  return (
    <DeductibleContext.Provider
      value={{
        refreshUserInfo,
        retrieveDeductiblePreview,
        ...deductiblePreview,
        ...reimbursementDetails,
        ...mockDeductibleInfo,
        updateUserBenefitEnrollment
      }}
    >
      {children}
    </DeductibleContext.Provider>
  )
}
